/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { httpService } from "../../../../services";
import { HTTP_METHOD } from "@nf/constants";
import { IPlayResponseModel, IUserAvailabilityKeyModel } from "../models";

interface IEntitlementAuthOptions {
  sessionToken?: string;
  authToken?: string;
}

const authHeaders = ({ sessionToken, authToken }: IEntitlementAuthOptions) =>
  sessionToken
    ? {
        Authorization: `Bearer ${authToken ?? sessionToken}`,
        "X-REDBEE-AUTH": `Bearer ${sessionToken}`,
      }
    : undefined;

export class EntitlementService {
  get url(): string {
    return `/v2/entitlement`;
  }

  public getAvailabilityKeys = (): Promise<IUserAvailabilityKeyModel> =>
    httpService.request<IUserAvailabilityKeyModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/availabilitykey`,
    });

  public play = async (
    assetId: string,
    options?: IEntitlementAuthOptions & {
      params?: Record<string, string | number | boolean>;
      supportedFormats?: string[];
      supportedDrms?: string[];
    }
  ): Promise<IPlayResponseModel> => {
    const { params, supportedFormats, supportedDrms } = options ?? {};

    return httpService.request<IPlayResponseModel>({
      url: `${this.url}/${assetId}/play`,
      method: HTTP_METHOD.GET,
      axiosConfig: {
        headers: authHeaders(options ?? {}),
        params: {
          ...params,
          ...(supportedFormats?.length
            ? { supportedFormats: supportedFormats.join(",") }
            : {}),
          ...(supportedDrms?.length
            ? { supportedDrms: supportedDrms.join(",") }
            : {}),
        },
      },
    });
  };

  public entitle = async (
    assetId: string,
    options?: IEntitlementAuthOptions & {
      time?: string;
    }
  ): Promise<void> => {
    await httpService.request({
      url: `${this.url}/${assetId}/entitle`,
      method: HTTP_METHOD.GET,
      axiosConfig: {
        headers: authHeaders(options ?? {}),
        ...(options?.time ? { params: { time: options.time } } : {}),
      },
    });
  };
}
